import React from "react";
import {
  ArrowDown,
  ArrowRight,
  Blocks,
  Boxes,
  CloudCog,
  Code2,
  Database,
  FileText,
  GitBranch,
  Layers3,
  Workflow,
} from "lucide-react";
import { Shell, PageSection, CTA } from "../components/SiteLayout";
import { servicePages, processSteps } from "../data/siteData";

export default function HomePage() {
  const homeServices = [
    ...servicePages.slice(0, 2),
    servicePages[2],
    servicePages[3],
    servicePages[4],
    servicePages[5],
  ];
  const icons = [Blocks, Database, Code2, Workflow, CloudCog, FileText];
  return (
    <Shell
      active="home"
      seo={{
        title: "Custom Software Development & Business Solutions",
        description:
          "RIOZ develops custom business software, information systems, web applications, integrations, and cloud infrastructure tailored to your business.",
        path: "/",
      }}
    >
      <section className="hero home-hero">
        <div className="hero-copy">
          <div className="eyebrow">
            <span className="status-dot" /> SOFTWARE SOLUTIONS & ENGINEERING{" "}
            <span className="eyebrow-region">· SERBIA & BEYOND</span>
          </div>
          <h1>
            Software solutions built around <span>your business.</span>
          </h1>
          <p className="hero-intro">
            We design and develop custom software, business information systems,
            web applications, integrations, and infrastructure that help
            companies work smarter and grow with confidence.
          </p>
          <div className="hero-actions">
            <a className="button-primary" href="/contact">
              Discuss your project <ArrowRight size={17} />
            </a>
            <a className="button-quiet" href="/services">
              Explore our services <ArrowDown size={15} />
            </a>
          </div>
          <div className="hero-note">
            <span className="note-line" /> From business requirements to
            production-ready software.
          </div>
        </div>
        <div className="hero-visual">
          <div className="visual-top">
            <span>END-TO-END DELIVERY</span>
            <span className="visual-live">
              <i /> READY TO BUILD
            </span>
          </div>
          <div className="visual-flow">
            <div className="flow-node node-main">
              <span className="node-icon">
                <Layers3 size={18} />
              </span>
              <span>
                <b>Business requirements</b>
                <small>Processes · People · Goals</small>
              </span>
              <span className="node-index">01</span>
            </div>
            <div className="flow-connector">
              <span />
            </div>
            <div className="flow-middle">
              <div className="flow-node node-small">
                <Code2 size={17} />
                <span>
                  <b>Applications</b>
                  <small>Built for your team</small>
                </span>
              </div>
              <div className="flow-node node-small">
                <GitBranch size={17} />
                <span>
                  <b>Integrations</b>
                  <small>Systems in sync</small>
                </span>
              </div>
            </div>
            <div className="flow-connector">
              <span />
            </div>
            <div className="flow-node node-main node-platform">
              <span className="node-icon">
                <Boxes size={18} />
              </span>
              <span>
                <b>Production platform</b>
                <small>Deployed · Monitored · Supported</small>
              </span>
              <span className="platform-pulse" />
            </div>
          </div>
          <div className="visual-foot">
            <span>ONE PARTNER, END TO END</span>
            <span>
              DESIGN <b>→</b> BUILD <b>→</b> RUN
            </span>
          </div>
        </div>
      </section>
      <PageSection
        eyebrow="BUILT AROUND YOUR REALITY"
        title="Technology should work for your business."
        intro="Every business has different processes, challenges, and goals. We help turn those challenges into practical software, combining application engineering, integrations, and infrastructure around real business needs."
      >
        <a className="text-link" href="/about">
          Discover how we work <ArrowRight size={15} />
        </a>
      </PageSection>
      <PageSection
        eyebrow="WHAT WE DO"
        title="Complete software solutions"
        intro="From individual applications to connected business systems, we provide engineering expertise to build and maintain your digital operations."
      >
        <div className="service-grid six-service-grid">
          {homeServices.map((s, i) => {
            const Icon = icons[i];
            return (
              <a
                className="service-card service-card-link"
                href={`/services/${s.slug}`}
                key={s.slug}
              >
                <div className="service-top">
                  <span className="service-number">0{i + 1}</span>
                  <span className="service-icon">
                    <Icon size={20} />
                  </span>
                </div>
                <h3>{s.title}</h3>
                <p>{s.intro}</p>
                <div className="service-examples">
                  {s.build.slice(0, 3).join(" · ")}
                </div>
                <span className="card-link">
                  Explore service <ArrowRight size={14} />
                </span>
              </a>
            );
          })}
        </div>
        <a className="text-link" href="/services">
          View all services <ArrowRight size={15} />
        </a>
      </PageSection>
      <PageSection
        eyebrow="WHY RIOZ"
        title="One technical partner. Complete solutions."
        intro="Successful software takes business understanding, sound engineering, thoughtful integrations, and care in production. RIOZ brings those capabilities together."
      >
        <div className="feature-grid">
          {[
            [
              "Business-driven engineering",
              "We start with your business requirements. Each solution is designed to address a real operational need.",
            ],
            [
              "End-to-end development",
              "Frontend, backend, databases, integrations, and infrastructure connected into one complete delivery.",
            ],
            [
              "Built for growth",
              "We prioritize maintainable architecture and long-term flexibility appropriate to your needs.",
            ],
            [
              "Long-term partnership",
              "After launch, we can help with maintenance, improvements, technical support, and infrastructure.",
            ],
          ].map(([t, d], i) => (
            <article className="feature-item" key={t}>
              <span className="service-number">0{i + 1}</span>
              <h3>{t}</h3>
              <p>{d}</p>
            </article>
          ))}
        </div>
      </PageSection>
      <PageSection
        eyebrow="HOW WE WORK"
        title="From idea to production."
        intro="A structured approach keeps the work transparent, focused, and aligned with business objectives."
      >
        <div className="steps-list home-steps">
          {processSteps.map(([n, t, d]) => (
            <div className="step" key={n}>
              <span className="step-number">{n}</span>
              <div>
                <h3>{t}</h3>
                <p>{d}</p>
              </div>
              <ArrowRight className="step-arrow" size={17} />
            </div>
          ))}
        </div>
        <a className="text-link" href="/process">
          Learn about our process <ArrowRight size={15} />
        </a>
      </PageSection>
      <PageSection
        eyebrow="ENGAGEMENT MODELS"
        title="Flexible ways to work together."
        intro="Choose an engagement that fits the clarity and scale of your project. We agree on scope, capacity, and expectations before work begins."
      >
        <div className="engagement-grid">
          {[
            [
              "Fixed-scope projects",
              "Defined deliverables, milestones, and an agreed project price.",
            ],
            [
              "Dedicated development",
              "Planned engineering capacity for ongoing or evolving work.",
            ],
            [
              "Technical partnership",
              "Continued development, maintenance, and infrastructure support.",
            ],
          ].map(([t, d]) => (
            <article className="engagement-card" key={t}>
              <h3>{t}</h3>
              <p>{d}</p>
            </article>
          ))}
        </div>
        <a className="text-link" href="/pricing">
          Explore engagement and pricing <ArrowRight size={15} />
        </a>
      </PageSection>
      <CTA
        title="Have a business challenge worth solving?"
        text="Let’s discuss how software can simplify operations, connect systems, or help you build something new."
        action="Start a conversation"
      />
    </Shell>
  );
}
